import { useEffect, useState } from "react";
import { RefreshCw, Star, TrendingDown, TrendingUp, Minus, Loader2, LogIn, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import useWatchlist from "@/hooks/useWatchlist";
import { analyzeWatchlist } from "@/lib/api";

function QuotaBanner({ quota }) {
  if (!quota) return null;
  const pct = Math.round((quota.remaining / quota.max) * 100);
  const low = quota.remaining <= 10;
  return (
    <div className={`flex items-center gap-2 text-xs px-3 py-2 border ${low ? "border-amber-500/30 bg-amber-500/8 text-amber-400" : "border-white/10 bg-white/3 text-white/45"}`}>
      <AlertCircle className="w-3 h-3 flex-shrink-0" />
      <span>
        <span className={low ? "text-amber-300 font-medium" : "text-white/70 font-medium"}>{quota.remaining}/{quota.max}</span>
        {" "}analisi di mercato rimanenti questa settimana
        {low && quota.remaining === 0 && " — quota esaurita"}
      </span>
    </div>
  );
}

function VerdictBadge({ verdict, savingPct }) {
  if (!verdict || verdict === "neutro") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/55">
        <Minus className="w-2.5 h-2.5" /> Neutro
      </span>
    );
  }
  if (verdict === "conveniente") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
        <TrendingDown className="w-2.5 h-2.5" />
        Affare {savingPct != null ? `+${Math.abs(savingPct).toFixed(1)}%` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400">
      <TrendingUp className="w-2.5 h-2.5" />
      Caro {savingPct != null ? `${savingPct.toFixed(1)}%` : ""}
    </span>
  );
}

function SavingDisplay({ savingPct }) {
  if (savingPct == null) return null;
  const isPositive = savingPct > 0;
  return (
    <span className={`text-sm font-medium ${isPositive ? "text-emerald-400" : "text-red-400"}`}>
      {isPositive ? "+" : ""}{savingPct.toFixed(1)}%
    </span>
  );
}

function LotAnalysisRow({ lot, result }) {
  const bid = lot.current_bid_eur ?? lot.starting_bid_eur ?? 0;
  const shipping = lot.shipping_eur ?? 0;
  const totalCost = result?.total_cost ?? (Math.round(((bid * 1.09) + 3 + shipping) * 100) / 100);

  return (
    <div className="border border-white/8 bg-white/2 hover:bg-white/3 transition-colors">
      <div className="flex gap-4 p-4">
        {/* Thumbnail */}
        {lot.image_url && (
          <a href={lot.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
            <img
              src={lot.image_url}
              alt={lot.title}
              className="w-16 h-16 object-cover opacity-90 hover:opacity-100 transition-opacity"
            />
          </a>
        )}

        {/* Lot info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <a
                href={lot.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/85 font-medium hover:text-gold transition-colors line-clamp-1 inline-flex items-center gap-1"
              >
                {result?.product_name || lot.title}
                <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-50" />
              </a>
              {lot.subtitle && (
                <p className="text-xs text-white/40 truncate mt-0.5">{lot.subtitle}</p>
              )}
            </div>
          </div>

          {/* Market analysis data */}
          {result?.quota_exceeded ? (
            <div className="mt-2 text-xs text-amber-400/70 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Quota esaurita — aggiorna il piano per nuove analisi
            </div>
          ) : result?.error ? (
            <p className="mt-2 text-xs text-red-400/70">Errore: {result.error}</p>
          ) : (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {/* Prezzo reale asta — prominente */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-0.5">Costo reale asta</p>
                <p className="text-xl font-semibold text-gold">€{totalCost.toFixed(2)}</p>
              </div>

              {result?.market_price != null && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-0.5">Prezzo mercato</p>
                  <p className="text-base text-white/70">€{result.market_price.toFixed(2)}</p>
                </div>
              )}

              {result?.min_price_italy != null && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-0.5">Min. Italia</p>
                  <p className="text-base text-white/70">€{result.min_price_italy.toFixed(2)}</p>
                </div>
              )}

              {result?.market_price != null && (
                <div className="flex flex-col gap-0.5">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30">Risparmio</p>
                  <div className="flex items-center gap-1.5">
                    <SavingDisplay savingPct={result.saving_pct} />
                    <VerdictBadge verdict={result.verdict} savingPct={result.saving_pct} />
                  </div>
                </div>
              )}

              {result?.market_price == null && !result?.quota_exceeded && !result?.error && (
                <p className="text-xs text-white/35 italic">Prezzo di mercato non trovato</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WatchlistEmpty() {
  return (
    <div className="py-20 text-center" data-testid="watchlist-empty">
      <Star className="w-10 h-10 mx-auto text-white/15 mb-4" />
      <h3 className="font-display text-2xl text-white/85 mb-3">
        Nessun lotto sotto osservazione
      </h3>
      <p className="text-sm text-white/55 max-w-md mx-auto">
        Sfoglia i risultati di ricerca e clicca la <strong>stellina</strong> in alto a destra
        di un lotto per tenerlo monitorato qui.
      </p>
    </div>
  );
}

function LoginPrompt({ onLogin }) {
  return (
    <div className="py-12 text-center border border-white/8 bg-white/2">
      <LogIn className="w-8 h-8 mx-auto text-white/25 mb-3" />
      <p className="text-sm text-white/60 mb-4">
        Accedi per vedere l'analisi di mercato e il confronto prezzi per i tuoi lotti.
      </p>
      <button
        type="button"
        onClick={onLogin}
        className="inline-flex items-center gap-2 bg-gold/10 hover:bg-gold/20 border border-gold/30 hover:border-gold/60 text-gold px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" />
        Accedi
      </button>
    </div>
  );
}

export default function WatchlistView({ user, onLogin }) {
  const { items, count, refresh } = useWatchlist();
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [quota, setQuota] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Auto-refresh lot data on mount
  useEffect(() => {
    if (count === 0) return;
    let cancelled = false;
    setRefreshing(true);
    (async () => {
      try {
        const res = await refresh();
        if (cancelled) return;
        setLastRefreshAt(new Date());
        if (res.refreshed > 0) {
          toast.success(`Aggiornati ${res.refreshed} lotti`, { duration: 2500 });
        }
      } catch {
        if (!cancelled) toast.error("Errore durante l'aggiornamento dei lotti.");
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-run market analysis when user is logged in and lots are available
  useEffect(() => {
    if (!user || count === 0) return;
    let cancelled = false;
    setAnalyzing(true);
    (async () => {
      try {
        const data = await analyzeWatchlist(items);
        if (cancelled) return;
        setAnalysisResults(data.results || []);
        setQuota(data.quota || null);
      } catch (e) {
        if (!cancelled) toast.error("Errore nell'analisi di mercato.");
      } finally {
        if (!cancelled) setAnalyzing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await refresh();
      setLastRefreshAt(new Date());
      toast.success(`Aggiornati ${res.refreshed} lotti`, { duration: 2500 });
    } catch {
      toast.error("Errore durante l'aggiornamento dei lotti.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div data-testid="watchlist-view">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold/80 mb-3">
            Sotto osservazione
          </p>
          <h2 className="font-display text-3xl sm:text-4xl text-white">
            {count === 0
              ? "Nessun lotto monitorato"
              : `I tuoi ${count} ${count === 1 ? "lotto" : "lotti"} monitorati`}
          </h2>
          {lastRefreshAt && (
            <p className="mt-2 text-xs text-white/45">
              Ultimo aggiornamento: {lastRefreshAt.toLocaleTimeString("it-IT")}
            </p>
          )}
        </div>
        {count > 0 && (
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-gold hover:text-gold px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
            data-testid="watchlist-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Aggiorno…" : "Aggiorna ora"}
          </button>
        )}
      </div>

      {count === 0 ? (
        <WatchlistEmpty />
      ) : (
        <>
          {/* Quota banner */}
          {user && quota && (
            <div className="mb-4">
              <QuotaBanner quota={quota} />
            </div>
          )}

          {/* Analysis loading state */}
          {user && analyzing && (
            <div className="mb-4 flex items-center gap-2 text-xs text-white/45 px-3 py-2 border border-white/8 bg-white/2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-gold" />
              Analisi prezzi di mercato in corso per {count} {count === 1 ? "lotto" : "lotti"}…
            </div>
          )}

          {/* Login prompt if not authenticated */}
          {!user && (
            <div className="mb-6">
              <LoginPrompt onLogin={onLogin} />
            </div>
          )}

          {/* Lot list with inline market analysis */}
          <div className="space-y-2">
            {items.map((lot, i) => {
              const result = analysisResults[i] ?? null;
              return (
                <LotAnalysisRow key={lot.id} lot={lot} result={user ? result : null} />
              );
            })}
          </div>

          <div className="mt-8 pt-6 border-t border-white/8 text-xs text-white/35">
            Per rimuovere un lotto, clicca di nuovo la stellina sulla card nei risultati di ricerca.
          </div>
        </>
      )}
    </div>
  );
}
